import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { DateRangeType } from "react-tailwindcss-datepicker";

import CustomDatePicker from "../../components/awaitingInventory/CustomDatePicker";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useMechanicDamageReport from "../../data/useMechanicDamageReport";
import { IMechanicDamageRow } from "../../models/stats/IMechanicDamageReport";
import classNames from "../../utils/classNames";
import toIsoDate from "../../utils/toIsoDate";

/**
 * Each mechanic's write-off rate against the team's: parts taken off, against parts marked damaged,
 * missing or not accessible at the vehicle instead. The rate is the figure to read - the split is
 * the mechanic's own choice of label, and "missing" gets used for broken parts.
 */
export default function MechanicDamageReport() {
  const { t } = useTranslation("reports");
  const { setInfo } = usePageNameContext();
  const now = DateTime.now();
  const [dates, setDates] = useState<DateRangeType>({
    startDate: now.startOf("month").toJSDate(),
    endDate: now.toJSDate(),
  });

  const from = useMemo(() => toIsoDate(dates.startDate), [dates.startDate]);
  const to = useMemo(() => toIsoDate(dates.endDate), [dates.endDate]);
  const { report } = useMechanicDamageReport(from, to);

  useEffect(() => {
    setInfo({
      name: t("Mechanic Damage Report"),
      desc: t("How often each mechanic writes parts off at the vehicle, against the team"),
    });
  }, [setInfo, t]);

  const data = report.data?.data;

  const cell = "px-3 py-3 border text-sm text-right tabular-nums";
  const rowCells = (row: IMechanicDamageRow) => (
    <>
      <td className={cell}>{row.removed}</td>
      <td className={classNames(cell, "font-semibold")}>{row.writtenOff}</td>
      <td className={classNames(cell, "font-semibold")}>{row.writeOffRate.toFixed(1)}%</td>
      <td className={classNames(cell, "text-gray-600")}>{row.damaged}</td>
      <td className={classNames(cell, "text-gray-600")}>{row.missing}</td>
      <td className={classNames(cell, "text-gray-600")}>{row.notAccessible}</td>
    </>
  );

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap items-end gap-4 bg-white p-4">
        <CustomDatePicker
          dates={dates}
          handleDatesChange={setDates}
          inputClassName="h-10 text-sm border-gray-300 rounded-md focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 w-64"
        />
        <p className="max-w-xl text-xs text-gray-500">
          {t("Written off is damaged, missing and not accessible together. Rows above the team's rate are highlighted. Damage found after a part came off isn't counted.")}
        </p>
      </div>

      {report.isLoading ? (
        <LoadingWheel />
      ) : report.isError ? (
        <p className="bg-white px-4 py-6 text-sm text-red-600">{(report.error as Error)?.message ?? t("The report couldn't be loaded.")}</p>
      ) : !data || data.mechanics.length === 0 ? (
        <p className="bg-white px-4 py-6 text-sm text-gray-500">{t("Nothing was removed or written off in this period.")}</p>
      ) : (
        <div className="overflow-auto bg-white">
          <table className="min-w-full divide-y divide-gray-300 border">
            <thead>
              <tr className="bg-primary-50 text-gray-800">
                <th className="px-3 py-3.5 text-left text-sm font-semibold">{t("Mechanic")}</th>
                <th className="px-3 py-3.5 text-right text-sm font-semibold">{t("Removed")}</th>
                <th className="px-3 py-3.5 text-right text-sm font-semibold">{t("Written off")}</th>
                <th className="px-3 py-3.5 text-right text-sm font-semibold">{t("Write-off rate")}</th>
                <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-600">{t("Damaged")}</th>
                <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-600">{t("Missing")}</th>
                <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-600">{t("Not accessible")}</th>
              </tr>
            </thead>
            <tbody>
              {data.mechanics.map((row) => (
                <tr key={row.userId ?? row.name} className={row.aboveTeamAverage ? "bg-red-50" : undefined}>
                  <td className="border px-3 py-3 text-sm font-medium text-gray-900">{row.name}</td>
                  {rowCells(row)}
                </tr>
              ))}
              <tr className="bg-gray-50 font-semibold">
                <td className="border px-3 py-3 text-sm text-gray-900">{t("Team")}</td>
                {rowCells(data.team)}
              </tr>
            </tbody>
          </table>

          {data.unattributed && (
            <p className="px-4 py-3 text-xs text-gray-500">
              {t("{{count}} write-offs have no name against them - they were marked before the system recorded who made each mark, and aren't counted in the team's rate.", { count: data.unattributed.writtenOff })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
